"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import Loader from "../../../Components/Loader"
import * as XLSX from "xlsx" // Import xlsx library
import xls from "../../../images/ReportsImages/xls.png" // Import xls image
import PageContainer from "../../components/PageContainer"
import Layout from "../../components/layout"
import SortIcon from "mdi-react/SortIcon"
import SortAscendingIcon from "mdi-react/SortAscendingIcon"
import SortDescendingIcon from "mdi-react/SortDescendingIcon"
import bse from "./bselogo.png"
import nse from "./NSE_Logo.svg"
import { Tooltip } from "antd"

export default function IPOTracker() {
  const [ipoData, setIpoData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [viewMode, setViewMode] = useState("table") // Default to table view
  const [searchTerm, setSearchTerm] = useState("")
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" })
  const [logoColors, setLogoColors] = useState({}) // Store extracted logo colors
  const [filter, setFilter] = useState("all") // Default filter is "all"

  const fetchData = async () => {
    try {
      setLoading(true)
      // Always fetch all data without any filter parameters
      const response = await axios.get("https://api-hub.ventureintelligence.com/public/startup-ipo/")
      setIpoData(response.data.data)
      setLoading(false)
    } catch (err) {
      setError("Failed to fetch IPO data. Please try again later.")
      setLoading(false)
      console.error("Error fetching IPO data:", err)
    }
  }

  useEffect(() => {
    fetchData()
  }, []) // Only fetch once when component mounts

  // Extract dominant color from image
  const extractDominantColor = (imageUrl, companyId) => {
    if (logoColors[companyId]) return // Already extracted

    const img = new Image()
    img.crossOrigin = "anonymous"

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas")
        const ctx = canvas.getContext("2d")

        canvas.width = img.width
        canvas.height = img.height

        ctx.drawImage(img, 0, 0)

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const data = imageData.data

        const colorCount = {}

        for (let i = 0; i < data.length; i += 40) {
          const r = data[i]
          const g = data[i + 1]
          const b = data[i + 2]
          const a = data[i + 3]

          // Skip transparent or very light pixels
          if (a < 128 || (r > 240 && g > 240 && b > 240)) continue

          const rgb = `${Math.floor(r / 10) * 10},${Math.floor(g / 10) * 10},${Math.floor(b / 10) * 10}`
          colorCount[rgb] = (colorCount[rgb] || 0) + 1
        }

        // Find most common color
        let dominantColor = "60,60,60" // Default gray
        let maxCount = 0

        for (const [color, count] of Object.entries(colorCount)) {
          if (count > maxCount) {
            maxCount = count
            dominantColor = color
          }
        }

        const [r, g, b] = dominantColor.split(",").map(Number)
        const hexColor = `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`

        setLogoColors((prev) => ({
          ...prev,
          [companyId]: hexColor,
        }))
      } catch (error) {
        console.error("Error extracting color:", error)
        setLogoColors((prev) => ({
          ...prev,
          [companyId]: "#bc8b20", // Fallback to theme color
        }))
      }
    }

    img.onerror = () => {
      setLogoColors((prev) => ({
        ...prev,
        [companyId]: "#bc8b20", // Fallback to theme color
      }))
    }

    img.src = imageUrl
  }

  // Format date to "Mon YYYY" (e.g., "Apr 2025")
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short" }
    return new Date(dateString).toLocaleDateString(undefined, options)
  }

  // Format price with currency symbol and commas (e.g., ₹1,329.00)
  const formatPrice = (price) => {
    const num = Number.parseFloat(price)
    if (isNaN(num)) return "-"
    return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  // Calculate price change and percentage
  const calculateChange = (currentPrice, ipoPrice) => {
    const current = Number.parseFloat(currentPrice)
    const ipo = Number.parseFloat(ipoPrice)
    const change = current - ipo
    const percentChange = (change / ipo) * 100

    return {
      change: change.toFixed(2),
      percentChange: percentChange.toFixed(2),
      isPositive: change >= 0,
    }
  }

  // Handle sorting
  const handleSort = (key) => {
    let direction = "asc"
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc"
    }
    setSortConfig({ key, direction })
  }

  // Function to download data as Excel
  const downloadExcel = () => {
    const dataToExport = filteredData.map((ipo) => ({
      "Company Name": ipo.company_name,
      "IPO Date": ipo.ipo_date,
      "IPO Price": ipo.ipo_price,
      "Adjusted IPO Price": ipo.ipo_price_adjusted === ipo.ipo_price ? "--" : ipo.ipo_price_adjusted,
      "Current Price": ipo.current_price,
      "BSE Code": ipo.bse_code,
      "NSE Code": ipo.nse_code,
    }))

    const worksheet = XLSX.utils.json_to_sheet(dataToExport)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "IPO Data")

    // Generate Excel file and trigger download
    XLSX.writeFile(workbook, "IPO_Data.xlsx")
  }

  // Filter data based on search term and tech/non-tech filter
  const filteredData = ipoData.filter((ipo) => {
    // Apply search filter
    const matchesSearch = ipo.company_name.toLowerCase().includes(searchTerm.toLowerCase())

    // Apply tech/non-tech filter
    let matchesFilter = true
    if (filter === "tech") {
      matchesFilter = ipo.is_tech === true
    } else if (filter === "non-tech") {
      matchesFilter = ipo.is_tech === false
    }

    return matchesSearch && matchesFilter
  })

  // Sort data based on sort config
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0

    let aValue = a[sortConfig.key]
    let bValue = b[sortConfig.key]

    // Handle different data types
    if (sortConfig.key === "ipo_date") {
      aValue = new Date(aValue)
      bValue = new Date(bValue)
    } else if (sortConfig.key === "ipo_price" || sortConfig.key === "current_price") {
      aValue = Number.parseFloat(aValue)
      bValue = Number.parseFloat(bValue)
    } else if (sortConfig.key === "ipo_price_adjusted") {
      // Handle special case for adjusted IPO price sorting
      const aFormatted = formatPrice(a.ipo_price)
      const bFormatted = formatPrice(b.ipo_price)
      const aAdjustedFormatted = formatPrice(a.ipo_price_adjusted)
      const bAdjustedFormatted = formatPrice(b.ipo_price_adjusted)

      // Check if values should display as "--"
      const aIsEmpty = aFormatted === aAdjustedFormatted
      const bIsEmpty = bFormatted === bAdjustedFormatted

      // If one is empty and other is not, prioritize the non-empty one
      if (aIsEmpty && !bIsEmpty) return sortConfig.direction === "asc" ? 1 : -1
      if (!aIsEmpty && bIsEmpty) return sortConfig.direction === "asc" ? -1 : 1

      // If both are empty or both have values, sort normally
      aValue = Number.parseFloat(aValue) || 0
      bValue = Number.parseFloat(bValue) || 0
    } else if (sortConfig.key === "company_name") {
      aValue = aValue.toLowerCase()
      bValue = bValue.toLowerCase()
    } else if (sortConfig.key === "percent_change") {
      const aChange =
        ((a.current_price - (a.ipo_price_adjusted || a.ipo_price)) / (a.ipo_price_adjusted || a.ipo_price)) * 100
      const bChange =
        ((b.current_price - (b.ipo_price_adjusted || b.ipo_price)) / (b.ipo_price_adjusted || b.ipo_price)) * 100
      aValue = aChange
      bValue = bChange
    }

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1
    return 0
  })

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">{error}</div>
        <button onClick={fetchData} className="ml-4 px-4 py-2 bg-[#bc8b20] text-white rounded-md hover:bg-[#a67a1c]">
          Retry
        </button>
      </div>
    )
  }

  // Get the most recent update date from the data
  const getMostRecentUpdateDate = () => {
    if (ipoData.length === 0) return null

    const dates = ipoData.map((ipo) => new Date(ipo.updated_at))
    const mostRecentDate = new Date(Math.max(...dates))

    const options = {
      year: "numeric",
      month: "long",
      day: "2-digit",
    }
    // Format: "May 28, 2025"
    return mostRecentDate.toLocaleDateString("en-US", options)
  }

  return (
    <PageContainer
      title="Startup IPO Tracker | Venture Intelligence"
      description="How are VC-backed startups faring in the public markets after their IPOs? This Venture Intelligence dashboard is a ready reckoner for each company's IPO date & price as well as current market price and performance."
    >
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Startup IPO Tracker</h1>
            <p className="text-gray-700 max-w-3xl mx-auto">
              How are VC-backed startups faring in the public markets after their IPOs? This Venture Intelligence
              dashboard is a ready reckoner for each company's IPO date & price as well as current market price and
              performance.
            </p>
          </div>

          {/* Controls - Search, Filter, View Toggle, and Download Button */}
          <div className="mb-6 flex justify-between items-center">
            {/* Search Bar */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  if (filter !== "all") setFilter("all")
                }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2"
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            {/* Filter Dropdown and View Toggle */}
            <div className="flex items-center space-x-4">
              {/* Filter Dropdown */}
              <div>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="border border-gray-300 rounded-md py-2 px-4 focus:ring-2"
                >
                  <option value="all">All Startups</option>
                  <option value="tech">Tech Startups</option>
                  <option value="non-tech">Non-Tech Startups</option>
                </select>
              </div>

              {/* View Toggle */}
              <div className="inline-flex rounded-md shadow-sm" role="group">
                <button
                  type="button"
                  onClick={() => setViewMode("table")}
                  title="Table View"
                  className={`p-2 text-sm font-medium rounded-l-lg border ${
                    viewMode === "table"
                      ? "bg-[#bc8b20] text-white border-[#bc8b20]"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("cards")}
                  title="Card View"
                  className={`p-2 text-sm font-medium rounded-r-lg border ${
                    viewMode === "cards"
                      ? "bg-[#bc8b20] text-white border-[#bc8b20]"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </button>
              </div>

              {/* Download Button */}
              <button onClick={downloadExcel} className="">
                <img src={xls || "/placeholder.svg"} alt="Download" className="h-6 w-5 mr-2" />
              </button>
            </div>
          </div>

          {/* Loader or Content */}
          {loading ? (
            <div className="w-full h-[17rem] bg-white flex items-center justify-center">
              <Loader />
            </div>
          ) : viewMode === "cards" ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedData.map((ipo) => {
                  const priceChange = calculateChange(ipo.current_price, ipo?.ipo_price_adjusted || ipo.ipo_price)
                  const borderColor = logoColors[ipo.id] || "#bc8b20"

                  // Extract color when logo loads
                  if (ipo.logo_link && !logoColors[ipo.id]) {
                    extractDominantColor(ipo.logo_link, ipo.id)
                  }

                  return (
                    <div
                      key={ipo.id}
                      className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-1 mb-2"
                    >
                      <div className="flex items-center p-4 border-b border-gray-100">
                        <div className="w-12 h-12 mr-4 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={ipo.logo_link || "/placeholder.svg?height=50&width=50"}
                            alt={`${ipo.company_name} logo`}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.onerror = null
                              e.target.src = `https://via.placeholder.com/50?text=${ipo.company_name.charAt(0)}`
                            }}
                          />
                        </div>
                        <div className="flex-1">
                          <h2 className="text-md font-semibold mb-1">
                            <a
                              href={ipo.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-900 hover:text-[#bc8b20]"
                            >
                              {ipo.company_name}
                            </a>
                          </h2>
                          {/* Old:
                          <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded">BSE: {ipo.bse_code}</span>
                          <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded">NSE: {ipo.nse_code}</span>
                          */}
                          <div className="text-xs bg-gray-100 px-2 py-1 rounded flex items-center space-x-1 w-fit">
                            {ipo.bse_code && (
                              <a
                                href={`https://www.bseindia.com/stock-share-price/company/symbol/${ipo.bse_code}/`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-900 underline decoration-transparent hover:decoration-[#bc8b20] hover:text-[#bc8b20] transition mb-2"
                                style={{ textUnderlineOffset: "2px" }}
                              >
                                {/* BSE link */}
                                <img src={bse || "/placeholder.svg"} width={36} height={36} />
                              </a>
                            )}
                            {ipo.bse_code && ipo.nse_code && <span className="mx-1 text-gray-400">/</span>}
                            {ipo.nse_code && (
                              <a
                                href={`https://www.nseindia.com/get-quotes/equity?symbol=${ipo.nse_code}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-900 underline decoration-transparent hover:decoration-[#bc8b20] hover:text-[#bc8b20] transition"
                                style={{ textUnderlineOffset: "2px" }}
                              >
                                <img src={nse || "/placeholder.svg"} width={36} height={36} />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="p-4">
                        <div className="flex justify-between py-2.5 border-b border-gray-100">
                          <span className="text-gray-700 font-medium text-sm">IPO Date</span>
                          <span className="text-gray-900 font-semibold text-sm">{formatDate(ipo.ipo_date)}</span>
                        </div>

                        <div className="flex justify-between py-2.5 border-b border-gray-100">
                          <span className="text-gray-700 font-medium text-sm">IPO Price^</span>
                          {formatPrice(ipo.ipo_price_adjusted) !== formatPrice(ipo.ipo_price) ? (
                            <Tooltip
                              title={`Original IPO price: ${formatPrice(ipo.ipo_price)}`}
                              placement="top"
                              overlayClassName="text-xs"
                            >
                              <span className="text-gray-900 font-semibold text-sm">
                                {formatPrice(ipo.ipo_price_adjusted)}^
                              </span>
                            </Tooltip>
                          ) : (
                            <span className="text-gray-900 font-semibold text-sm">{formatPrice(ipo.ipo_price)}</span>
                          )}
                        </div>

                        <div className="flex justify-between py-2.5 border-b border-gray-100">
                          <span className="text-gray-700 font-medium text-sm">Current Price</span>
                          <span className="text-gray-900 font-semibold text-sm">{formatPrice(ipo.current_price)}</span>
                        </div>

                        <div className="flex justify-between py-2.5">
                          <span className="text-gray-700 font-medium text-sm">Change</span>
                          <span
                            className={`font-semibold text-sm ${priceChange.isPositive ? "text-green-600" : "text-red-600"}`}
                          >
                            {priceChange.isPositive ? "+" : ""}
                            {formatPrice(priceChange.change)} ({priceChange.isPositive ? "+" : ""}
                            {priceChange.percentChange}%)
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Results count */}
              <div className="mt-6 text-center text-gray-700">
                Showing {sortedData.length} of {ipoData.length} IPOs
                {searchTerm && ` (filtered by "${searchTerm}")`}
              </div>
            </>
            // Card View
          ) : (
            // Table View
            <>
              <div className="flex justify-end mb-4">
                <p className="text-sm text-gray-600 ">*as on {getMostRecentUpdateDate()}</p>
              </div>
              <div className="overflow-x-auto">
                <div className="min-w-full">
                  {/* Header - Reduced BSE/NSE codes space from 1.5fr to 1fr */}
                  <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] px-6 py-3 bg-gray-50 rounded-t-xl text-xs font-medium items-center text-gray-700  tracking-wider">
                    <div className="px-3">
                      <button
                        onClick={() => handleSort("company_name")}
                        className="flex items-center hover:text-[#bc8b20] cursor-pointer text-sm"
                      >
                        Company
                        {sortConfig.key === "company_name" ? (
                          sortConfig.direction === "asc" ? (
                            <SortAscendingIcon className="pl-2 block h-[15px]" />
                          ) : (
                            <SortDescendingIcon className="pl-2 block h-[15px]" />
                          )
                        ) : (
                          <SortIcon className="pl-2 block h-[15px]" />
                        )}
                      </button>
                    </div>
                    <div className="px-3">
                      <button
                        onClick={() => handleSort("ipo_date")}
                        className="flex items-center hover:text-[#bc8b20] cursor-pointer text-sm"
                      >
                        IPO Date
                        {sortConfig.key === "ipo_date" ? (
                          sortConfig.direction === "asc" ? (
                            <SortAscendingIcon className="pl-2 block h-[15px]" />
                          ) : (
                            <SortDescendingIcon className="pl-2 block h-[15px]" />
                          )
                        ) : (
                          <SortIcon className="pl-2 block h-[15px]" />
                        )}
                      </button>
                    </div>
                    <div className="px-3">
                      <Tooltip title="^ Adjusted for Stock Split, Bonus, etc" placement="top" overlayClassName="text-xs">
                        <button
                          onClick={() => handleSort("ipo_price")}
                          className="flex items-center hover:text-[#bc8b20] cursor-pointer text-sm"
                        >
                          IPO Price^
                          {sortConfig.key === "ipo_price" || sortConfig.key === "ipo_price_adjusted" ? (
                            sortConfig.direction === "asc" ? (
                              <SortAscendingIcon className="pl-2 block h-[15px]" />
                            ) : (
                              <SortDescendingIcon className="pl-2 block h-[15px]" />
                            )
                          ) : (
                            <SortIcon className="pl-2 block h-[15px]" />
                          )}
                        </button>
                      </Tooltip>
                    </div>
                    <div className="px-3">
                      <button
                        onClick={() => handleSort("current_price")}
                        className="flex items-center hover:text-[#bc8b20] cursor-pointer text-sm"
                      >
                        Current Price&nbsp;*
                        {sortConfig.key === "current_price" ? (
                          sortConfig.direction === "asc" ? (
                            <SortAscendingIcon className="pl-2 block h-[15px]" />
                          ) : (
                            <SortDescendingIcon className="pl-2 block h-[15px]" />
                          )
                        ) : (
                          <SortIcon className="pl-2 block h-[15px]" />
                        )}
                      </button>
                    </div>
                    <div className="px-3">
                      <button
                        onClick={() => handleSort("percent_change")}
                        className="flex items-center hover:text-[#bc8b20] cursor-pointer text-sm"
                      >
                        % Change
                        {sortConfig.key === "percent_change" ? (
                          sortConfig.direction === "asc" ? (
                            <SortAscendingIcon className="pl-2 block h-[15px]" />
                          ) : (
                            <SortDescendingIcon className="pl-2 block h-[15px]" />
                          )
                        ) : (
                          <SortIcon className="pl-2 block h-[15px]" />
                        )}
                      </button>
                    </div>
                    <div className="px-3 text-sm">BSE/NSE</div>
                  </div>

                  {/* Table Body - Updated grid and added logo color border */}
                  <div className="space-y-4 mt-4">
                    {sortedData.map((ipo) => {
                      const priceChange = calculateChange(ipo?.current_price, ipo?.ipo_price_adjusted || ipo?.ipo_price)
                      const borderColor = logoColors[ipo.id] || "#bc8b20"

                      // Extract color when logo loads
                      if (ipo.logo_link && !logoColors[ipo.id]) {
                        extractDominantColor(ipo.logo_link, ipo.id)
                      }

                      return (
                        <div
                          key={ipo.id}
                          className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] px-6 py-4 bg-white rounded-xl border border-gray-200 shadow-lg hover:shadow-xl transition-shadow"
                          style={{ borderRight: `4px solid ${borderColor}` }}
                        >
                          {/* Company */}
                          <div className="flex items-center px-3 relative after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-gradient-to-b after:from-gray-100 after:via-gray-200 after:to-gray-100 after:rounded-full after:shadow-[1px_0_3px_rgba(0,0,0,0.1)]">
                            <div className="flex-shrink-0 h-10 w-10 rounded-md overflow-hidden">
                              <img
                                src={ipo.logo_link || "/placeholder.svg?height=40&width=40"}
                                alt={`${ipo.company_name} logo`}
                                className="h-10 w-10 object-contain"
                                // onError={(e) => {
                                //   e.target.onerror = null
                                //   e.target.src = `https://via.placeholder.com/40?text=${ipo.company_name.charAt(0)}`
                                // }}
                              />
                            </div>
                            <div className="ml-4 flex-1">
                              <a
                                href={ipo.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-gray-900 hover:text-[#bc8b20] block"
                              >
                                {ipo.company_name}
                              </a>
                            </div>
                          </div>

                          {/* IPO Date */}
                          <div className="flex items-center px-3 text-sm text-gray-900 relative after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-gradient-to-b after:from-gray-100 after:via-gray-200 after:to-gray-100 after:rounded-full after:shadow-[1px_0_3px_rgba(0,0,0,0.1)]">
                            {formatDate(ipo.ipo_date)}
                          </div>

                          {/* IPO Price */}
                          <div className="flex items-center px-3 text-sm text-gray-900 relative after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-gradient-to-b after:from-gray-100 after:via-gray-200 after:to-gray-100 after:rounded-full after:shadow-[1px_0_3px_rgba(0,0,0,0.1)]">
                            {formatPrice(ipo.ipo_price_adjusted) !== formatPrice(ipo.ipo_price) ? (
                              <Tooltip
                                title={`Original IPO price: ${formatPrice(ipo.ipo_price)}`}
                                placement="top"
                                overlayClassName="text-xs"
                              >
                                <span>{formatPrice(ipo.ipo_price_adjusted)}^</span>
                              </Tooltip>
                            ) : (
                              <span>{formatPrice(ipo.ipo_price)}</span>
                            )}
                          </div>

                          {/* Current Price */}
                          <div className="flex items-center px-3 text-sm text-gray-900 relative after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-gradient-to-b after:from-gray-100 after:via-gray-200 after:to-gray-100 after:rounded-full after:shadow-[1px_0_3px_rgba(0,0,0,0.1)]">
                            {formatPrice(ipo.current_price)}
                          </div>

                          {/* Change */}
                          <div className="flex items-center px-3 relative after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-gradient-to-b after:from-gray-100 after:via-gray-200 after:to-gray-100 after:rounded-full after:shadow-[1px_0_3px_rgba(0,0,0,0.1)]">
                            <span
                              className={`text-sm ${priceChange.isPositive ? "text-green-600" : "text-red-600"} font-medium`}
                            >
                              {priceChange.isPositive ? "+" : ""}
                              {formatPrice(priceChange.change)} ({priceChange.isPositive ? "+" : ""}
                              {priceChange.percentChange}%)
                            </span>
                          </div>

                          {/* BSE/NSE Codes - Reduced space and simplified layout */}
                          <div className="flex items-center px-3 space-x-1">
                            {ipo.bse_code && (
                              <a
                                href={`https://www.bseindia.com/stock-share-price/company/symbol/${ipo.bse_code}/`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-900 underline decoration-transparent hover:decoration-[#bc8b20] hover:text-[#bc8b20] transition text-xs font-medium mb-2"
                                style={{ textUnderlineOffset: "2px" }}
                              >
                                {/* BSE link */}
                                <img src={bse || "/placeholder.svg"} width={43} height={43} />
                              </a>
                            )}
                            {ipo.bse_code && ipo.nse_code && <span className="mx-1 text-gray-400">/</span>}
                            {ipo.nse_code && (
                              <a
                                href={`https://www.nseindia.com/get-quotes/equity?symbol=${ipo.nse_code}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-900 underline decoration-transparent hover:decoration-[#bc8b20] hover:text-[#bc8b20] transition text-xs font-medium"
                                style={{ textUnderlineOffset: "2px" }}
                              >
                                {/* NSE link */}
                                <img src={nse || "/placeholder.svg"} width={43} height={43} />
                              </a>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
              {/* Results count */}
              <div className="mt-6 text-center text-gray-700">
                Showing {sortedData.length} of {ipoData.length} IPOs
                {searchTerm && ` (filtered by "${searchTerm}")`}
              </div>
            </>
          )}
        </div>
      </Layout>
    </PageContainer>
  )
}
